import React, { FC } from 'react';

import { Box, styled, Typography } from '@mui/material';
import { css } from '@mui/material/styles';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import { BodyText } from '~/components/UI/Texts';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import PlanFeatures from '~/libs/xunl/pricing/PriceCard/PlanFeatures';
import PlanPrice from '~/libs/xunl/pricing/PriceCard/PlanPrice';
import SelectPlanButton from '~/libs/xunl/pricing/SelectPlanButton/SelectPlanButton';
import {
  MIDDLE_RADIUS_PX,
  MIDDLE_MARGIN_PX,
  BIG_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  SMALL_RADIUS_PX,
  SMALL_MARGIN_PX,
} from '~/theme';
import { Pricing } from '~/types';

type CardVariant = 'default' | 'wide';

export const PriceCardSkeleton: FC<Pick<PriceCardProps, 'variant'>> = ({ variant = 'default' }) => {
  if (variant === 'wide') {
    return (
      <StyledContainer data-testid="PriceCardSkeleton" variant="wide">
        <AnimatedSkeleton sx={{ flexShrink: 0, height: '296px', width: '420px' }} />
        <Box display="flex" flexDirection="column" width="100%" gap={{ md: '18px' }}>
          <AnimatedSkeleton sx={{ height: '48px', width: '100%' }} />
          <Box display="flex" flexDirection="column" gap={'16px'}>
            {new Array(4).fill(null).map((_, index) => (
              <AnimatedSkeleton key={index} sx={{ height: '26px', width: '100%' }} />
            ))}
          </Box>
        </Box>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer data-testid="PriceCardSkeleton" variant="default">
      <StyledPanelHeading variant="default">
        <Box display="flex" flexDirection="column" width="100%" gap={{ md: '4px' }}>
          <AnimatedSkeleton sx={{ height: { xs: '28px', lg: '48px' }, width: '56%' }} />
          <AnimatedSkeleton sx={{ height: '26px', width: '70%' }} />
        </Box>
        <AnimatedSkeleton sx={{ height: { xs: '64px', lg: '68px' }, width: '50%' }} />
        <AnimatedSkeleton sx={{ height: '52px', width: '100%', borderRadius: '999em' }} />
      </StyledPanelHeading>
      <Box display="flex" flexDirection="column" gap={{ md: '18px' }}>
        <AnimatedSkeleton sx={{ height: '48px', width: '100%' }} />
        <Box display="flex" flexDirection="column" gap={'16px'}>
          {new Array(4).fill(null).map((_, index) => (
            <AnimatedSkeleton key={index} sx={{ height: '26px', width: '100%' }} />
          ))}
        </Box>
      </Box>
    </StyledContainer>
  );
};

export type PriceCardProps = {
  isAnnual: boolean;
  isIndividual: boolean;
  plan: Pricing;
  hasDurationsChoices: boolean;
  variant?: CardVariant;
  onSelectPlan: (planId?: string | null) => void;
};

export const PriceCard: FC<PriceCardProps> = ({
  isAnnual,
  isIndividual,
  plan,
  hasDurationsChoices,
  variant = 'default',
  onSelectPlan,
}) => {
  const {
    id,
    description,
    enterprise,
    features,
    title,
    subtitle,
    chargebee_plan_info: chargebeePlanInfo,
    features_heading: featuresHeading,
    free_trial_period: trialPeriod,
  } = plan;

  const isEnterprise = Boolean(enterprise);

  const { t } = useTenantTranslation();

  const isTaxable = Boolean(chargebeePlanInfo?.is_taxable);
  const hasTrialPeriod = Boolean(trialPeriod && trialPeriod.count && trialPeriod.unit);
  const trialPeriodText = t(`common.${trialPeriod?.unit}`, { count: trialPeriod?.count || 0 });
  const trialPeriodTextEnding = t('page.price.freeTrial');

  const planFeatures = [...features];
  if (hasTrialPeriod) {
    planFeatures.push(`${trialPeriodText} ${trialPeriodTextEnding}`);
  }

  const price = chargebeePlanInfo?.is_taxable
    ? chargebeePlanInfo?.price_with_tax
    : chargebeePlanInfo?.price;

  return (
    <StyledContainer data-testid={`PriceCard:${id}`} variant={variant}>
      <StyledPanelHeading variant={variant}>
        <Box gap={{ md: '4px' }}>
          <StyledTitle variant="h3">
            {title} {description && <StyledCaption>{description}</StyledCaption>}
          </StyledTitle>
          {subtitle && <StyledBodyText>{subtitle}</StyledBodyText>}
        </Box>
        {isEnterprise ? (
          <StyledEnterprisePrice>{t('page.price.enterpriseHeader')}</StyledEnterprisePrice>
        ) : (
          <PlanPrice
            taxLabel={isIndividual && isTaxable ? t('page.price.inclVat') : t('page.price.exclVat')}
            price={price}
            discounted={hasDurationsChoices && isAnnual}
            periodLabel={isAnnual ? t('page.price.perYear') : t('page.price.perMonth')}
          />
        )}
        <SelectPlanButton
          variant={variant === 'wide' ? 'sticky' : 'default'}
          priceData={plan}
          onClick={onSelectPlan}
        />
      </StyledPanelHeading>
      <PlanFeatures features={planFeatures} title={featuresHeading} />
    </StyledContainer>
  );
};

const StyledContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'variant',
})<Pick<PriceCardProps, 'variant'>>`
  background: ${({ theme }) => theme.palette.common.blackSecondary};
  border-radius: ${MIDDLE_RADIUS_PX};
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${MIDDLE_MARGIN_PX};
  padding: ${SUBMIDDLE_MARGIN_PX} ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('lg')} {
    padding: ${MIDDLE_MARGIN_PX};
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    ${({ variant }) =>
      variant === 'wide' &&
      css`
        align-items: center;
        flex-direction: row;
        gap: ${BIG_MARGIN_PX};
      `}
  }
`;
const StyledPanelHeading = styled('div', {
  shouldForwardProp: (prop) => prop !== 'variant',
})<Pick<PriceCardProps, 'variant'>>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SUBMIDDLE_MARGIN_PX};

  ${({ theme, variant }) =>
    variant === 'wide' &&
    css`
      background-color: ${theme.palette.common.card.background.light};
      border-radius: ${SMALL_RADIUS_PX};
      padding: ${MIDDLE_MARGIN_PX};
    `}
  ${({ theme }) => theme.breakpoints.up('md')} {
    min-width: ${({ variant }) => (variant === 'wide' ? '420px' : 'unset')};
  }
`;
const StyledTitle = styled(Typography)`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.common.violetSecondary};

  ${({ theme }) => theme.breakpoints.up('lg')} {
    font-size: 40px;
  }
`;
const StyledCaption = styled(Typography)`
  display: none;

  ${({ theme }) => theme.breakpoints.up('xl')} {
    display: inline;
    font-size: 18px;
  }
`;
const StyledBodyText = styled(BodyText)`
  color: ${({ theme }) => theme.palette.common.white};
`;
const StyledEnterprisePrice = styled(Typography)`
  font-size: 54px;
  font-weight: 700;
  line-height: 68px;

  ${({ theme }) => theme.breakpoints.up('lg')} {
    font-size: 64px;
    line-height: 72px;
  }
`;
